import { useRef, useState } from 'react'
import { useSetting, readSetting } from './useSetting'

/**
 * Row/column-wide draggable resizing for a uniform CSS grid of chart cells,
 * shared by ChartsPage and InstrumentDetails.
 *
 * Sizes are tracked as fraction arrays per layout preset and persisted via
 * useSetting (server-synced). A live override mutates on every pointermove and
 * is committed to settings on pointerup, so we don't write-through mid-drag.
 * `resetForLayoutChange` wipes every saved preset — switching layouts starts
 * every layout (including the one returned to) with equal tracks.
 */
type GridSizes = Record<string, { cols: number[]; rows: number[] }>
const MIN_FRAC_RATIO = 0.12 // a track can't shrink below 12% of its pair

function fracsFor(saved: number[] | undefined, n: number): number[] {
  if (saved && saved.length === n && saved.every((x) => x > 0 && isFinite(x))) return saved
  return Array(n).fill(1)
}

export interface GridResize {
  gridRef: React.RefObject<HTMLDivElement>
  gridTemplateColumns: string
  gridTemplateRows: string
  colFracs: number[]
  rowFracs: number[]
  startDrag: (axis: 'col' | 'row', index: number) => (e: React.PointerEvent) => void
  resetForLayoutChange: () => void
}

export function useGridResize(
  storageKey: string,
  layout: string,
  cols: number,
  rows: number,
): GridResize {
  const [sizes, setSizes] = useSetting<GridSizes>(storageKey, {})
  const colFracsSaved = fracsFor(sizes[layout]?.cols, cols)
  const rowFracsSaved = fracsFor(sizes[layout]?.rows, rows)
  const [liveCols, setLiveCols] = useState<number[] | null>(null)
  const [liveRows, setLiveRows] = useState<number[] | null>(null)
  const colFracs = liveCols ?? colFracsSaved
  const rowFracs = liveRows ?? rowFracsSaved
  const gridRef = useRef<HTMLDivElement>(null)

  const startDrag =
    (axis: 'col' | 'row', index: number) => (e: React.PointerEvent) => {
      e.preventDefault()
      const el = gridRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const isCol = axis === 'col'
      const base = isCol ? [...colFracs] : [...rowFracs]
      const size = isCol ? rect.width : rect.height
      const start = isCol ? e.clientX : e.clientY
      const total = base.reduce((a, b) => a + b, 0)
      const pair = base[index] + base[index + 1]
      const minF = pair * MIN_FRAC_RATIO
      let latest = base
      const move = (ev: PointerEvent) => {
        const pos = isCol ? ev.clientX : ev.clientY
        const df = ((pos - start) / size) * total
        const a = Math.min(Math.max(base[index] + df, minF), pair - minF)
        const next = [...base]
        next[index] = a
        next[index + 1] = pair - a
        latest = next
        if (isCol) setLiveCols(next)
        else setLiveRows(next)
      }
      const up = () => {
        window.removeEventListener('pointermove', move)
        window.removeEventListener('pointerup', up)
        const prev = readSetting<GridSizes>(storageKey, {})
        const cur = prev[layout] ?? { cols: colFracsSaved, rows: rowFracsSaved }
        setSizes({
          ...prev,
          [layout]: {
            cols: isCol ? latest : cur.cols,
            rows: isCol ? cur.rows : latest,
          },
        })
        if (isCol) setLiveCols(null)
        else setLiveRows(null)
      }
      window.addEventListener('pointermove', move)
      window.addEventListener('pointerup', up)
    }

  const resetForLayoutChange = () => {
    setLiveCols(null)
    setLiveRows(null)
    setSizes({})
  }

  return {
    gridRef,
    gridTemplateColumns: colFracs.map((f) => `minmax(0, ${f}fr)`).join(' '),
    gridTemplateRows: rowFracs.map((f) => `minmax(0, ${f}fr)`).join(' '),
    colFracs,
    rowFracs,
    startDrag,
    resetForLayoutChange,
  }
}
